import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../app/hooks';
import { getValueFromResource } from '../../common/timeLiveFunction';
import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
import MultiSelectDropdown, { MultiSelectOption } from '../../common/Multiselectdropdown';
import FilterAccordion from '../../common/FilterAccordion';

interface FilterProps {
    onApplyFilter: (filters: any) => void;
    onClearFilter: () => void;
}

type OptionType = MultiSelectOption;
const ALL_OPTION: OptionType = { value: '__all__', label: 'All' };

const ScheduleVSTrackingFilter: React.FC<FilterProps> = ({ onApplyFilter, onClearFilter }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [client, setClient] = useState<OptionType[]>([]);
    const [clientOptions, setClientOptions] = useState<OptionType[]>([]);
    const [project, setProject] = useState<OptionType[]>([]);
    const [projectsOptions, setProjectsOptions] = useState<OptionType[]>([]);
    const [task, setTask] = useState<OptionType[]>([]);
    const [taskOptions, setTaskOptions] = useState<OptionType[]>([]);
    const toCommaIds = (options: OptionType[]) => options.map((o) => o.value).join(',');
    const activeFilterCount = useMemo(
        () => [client, project, task].filter((group) => group.length > 0).length,
        [client, project, task]
    );

    const handleApply = () => {
        const filters = {
            client: toCommaIds(client),
            project: toCommaIds(project),
            task: toCommaIds(task),
        };
        onApplyFilter(filters);
        setIsOpen(false);
    };

    const handleClear = () => {
        setClient([]);
        setProject([]);
        setTask([]);
        setProjectsOptions([]);
        setTaskOptions([]);
        onClearFilter();
    };

    const getClientsDropdownData = async () => {
        try {
            const response = await fetchTimeliveAPI(`api/Clients`, 'GET', '', true);
            const json = await response.json();
            if (json?.StatusCode === '200') {
                const options: OptionType[] = (json?.Data?.map((item: any) => ({
                    value: item?.AccountPartyId,
                    label: item?.PartyName
                })) || []);
                setClientOptions(options);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getProjectDropdownData = async (clientIds: any[]) => {
        if (!clientIds || clientIds.length === 0) {
            setProjectsOptions([]);
            setProject([]);
            return;
        }
        try {
            const idsParam = clientIds.join(',');
            const response = await fetchTimeliveAPI(`api/Project/GetAccountProjects/${idsParam}`, 'GET', '', true);
            const json = await response.json();
            if (json?.StatusCode === '200') {
                const options: OptionType[] = (json?.Data?.map((item: any) => ({
                    value: item?.AccountProjectId,
                    label: item?.ProjectName
                })) || []);
                setProjectsOptions(options);
                setProject((prev) => prev.filter((p) => options.some((o) => o.value === p.value)));
            } else {
                setProjectsOptions([]);
                setProject([]);
            }
        } catch (error) {
            console.log(error);
            setProjectsOptions([]);
            setProject([]);
        }
    };

    const getProjectTaskDropdownData = async (projectIds: any[]) => {
        if (!projectIds || projectIds.length === 0) {
            setTaskOptions([]);
            setTask([]);
            return;
        }
        try {
            const idsParam = projectIds.join(',');
            const response = await fetchTimeliveAPI(`api/Task/GetAccountProjectTaskDropdown/${idsParam}`, 'GET', '', true);
            const json = await response.json();
            if (json?.StatusCode === '200') {
                const options: OptionType[] = (json?.Data?.map((item: any) => ({
                    value: item?.AccountProjectTaskId,
                    label: item?.TaskName
                })) || []);
                setTaskOptions(options);
                setTask((prev) => prev.filter((tsk) => options.some((o) => o.value === tsk.value)));
            } else {
                setTaskOptions([]);
                setTask([]);
            }
        } catch (error) {
            console.log(error);
            setTaskOptions([]);
            setTask([]);
        }
    };

    useEffect(() => {
        getClientsDropdownData();
    }, []);

    useEffect(() => {
        const ids = client.map((c) => c.value);
        getProjectDropdownData(ids);
    }, [client]);

    useEffect(() => {
        const ids = project.map((p) => p.value);
        getProjectTaskDropdownData(ids);
    }, [project]);

    const withAllOption = (options: OptionType[]) =>
        options.length > 0 ? [ALL_OPTION, ...options] : options;

    const valueWithAll = (options: OptionType[], selected: OptionType[]) =>
        options.length > 0 && selected.length === options.length ? [ALL_OPTION, ...selected] : selected;

    const makeAllAwareHandler = (options: OptionType[], current: OptionType[], setter: (v: OptionType[]) => void) => (selected: OptionType[]) => {
        const hasAllNow = selected.some((o) => o.value === ALL_OPTION.value);
        const hadAllBefore = options.length > 0 && current.length === options.length;

        if (hasAllNow && !hadAllBefore) {
            setter(options);
        } else if (!hasAllNow && hadAllBefore) {
            setter([]);
        } else {
            setter(selected.filter((o) => o.value !== ALL_OPTION.value));
        }
    };

    return (
        <FilterAccordion
            title={getValueFromResource('Filters', dispatch, t)}
            open={isOpen}
            onToggle={setIsOpen}
            activeCount={activeFilterCount}
            onClear={handleClear}
            footer={
                <>
                    {/* <button className="btn btn-light btn-sm px-5" onClick={handleClear}>
                        {getValueFromResource('Clear All', dispatch, t)}
                    </button> */}
                    <button className="btn btn-primary btn-sm px-5" onClick={handleApply}>
                        {getValueFromResource('Apply Filters', dispatch, t)}
                    </button>
                </>
            }
        >
            <div className="row g-4">
                <div className="col-12 col-sm-6 col-md-3">
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Client', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        // value={client}
                        // onChange={setClient}
                        // options={clientOptions}
                        value={valueWithAll(clientOptions, client)}
                        onChange={makeAllAwareHandler(clientOptions, client, setClient)}
                        options={withAllOption(clientOptions)}
                        name="client"
                        isClearable
                        placeholder={getValueFromResource('Select', dispatch, t)}
                    />
                </div>

                <div className="col-12 col-sm-6 col-md-3">
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Project', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        // value={project}
                        // onChange={setProject}
                        // options={projectsOptions}
                        value={valueWithAll(projectsOptions, project)}
                        onChange={makeAllAwareHandler(projectsOptions, project, setProject)}
                        options={withAllOption(projectsOptions)}
                        name="project"
                        isClearable
                        isDisabled={client.length === 0}
                        placeholder={getValueFromResource('Select', dispatch, t)}
                    />
                </div>

                <div className="col-12 col-sm-6 col-md-3">
                    <label className="fs-7 fw-normal mb-2">
                        {getValueFromResource('Task', dispatch, t)}
                    </label>
                    <MultiSelectDropdown
                        // value={task}
                        // onChange={setTask}
                        // options={taskOptions}
                        value={valueWithAll(taskOptions, task)}
                        onChange={makeAllAwareHandler(taskOptions, task, setTask)}
                        options={withAllOption(taskOptions)}
                        name="task"
                        isClearable
                        isDisabled={project.length === 0}
                        placeholder={getValueFromResource('Select', dispatch, t)}
                    />
                </div>
            </div>
        </FilterAccordion>
    );
};

export default ScheduleVSTrackingFilter;